import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/**
 * Named documents on an equipment type or a unit (ClickUp z8nrz7cnj9).
 *
 * `name` is what the user typed and what the list is sorted and searched by;
 * `fileName` is what they uploaded. They are independent, so two documents may
 * carry the same file name and still be told apart.
 *
 * Exactly one of `equipmentTypeId` / `equipmentId` is set — that is the owner.
 */
export interface EquipmentDocument {
  id: string
  equipmentTypeId: string | null
  equipmentId: string | null
  name: string
  fileName: string
  mimeType: string
  sizeBytes: number
  uploadedBy: string
  uploadedAt: string
}

/**
 * A unit's documents, split by where they come from.
 *
 * `inherited` are its TYPE's documents — shown on the unit but editable only on
 * the type, because they describe the kind of machine and changing one from a
 * single unit would silently change what every other unit shows.
 */
export interface EquipmentDocumentLists {
  own: EquipmentDocument[]
  inherited: EquipmentDocument[]
}

/** Everything the browser needs to PUT the file straight to storage. */
interface PresignedUpload {
  url: string
  method: 'PUT'
  headers: Record<string, string>
  storageKey: string
  expiresInSeconds: number
  fileName: string
  mimeType: string
  sizeBytes: number
}

export interface EquipmentDocumentDownload {
  url: string
  fileName: string
  mimeType: string
  sizeBytes: number
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // `headers` must be spread LAST — see the same note in attachmentsApi.ts:
  // letting `...init` clobber them silently drops the bearer token.
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) {
        message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

const json = (body: unknown): RequestInit => ({
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})

/**
 * Three-step upload, matching the backend contract: presign → PUT the bytes
 * directly to storage → confirm.
 *
 * The middle step deliberately uses a bare `fetch` with NO auth headers: the
 * presigned URL carries its own signature, and sending an Authorization header
 * alongside it makes S3 reject the request. `presigned.headers` must be
 * forwarded verbatim or the signature won't match.
 */
async function upload(ownerPath: string, name: string, file: File): Promise<EquipmentDocument> {
  const presigned = await request<PresignedUpload>(`${ownerPath}/documents/presign`, {
    method: 'POST',
    ...json({ fileName: file.name, mimeType: file.type, sizeBytes: file.size }),
  })

  const put = await fetch(presigned.url, {
    method: presigned.method,
    headers: presigned.headers,
    body: file,
  })
  if (!put.ok) {
    throw new Error(`Upload failed (${put.status}). The file was not saved.`)
  }

  // Only this step creates the row, so an abandoned upload leaves no orphan.
  return request<EquipmentDocument>(`${ownerPath}/documents`, {
    method: 'POST',
    ...json({
      name,
      storageKey: presigned.storageKey,
      fileName: presigned.fileName,
      mimeType: presigned.mimeType,
      sizeBytes: presigned.sizeBytes,
    }),
  })
}

export const equipmentDocumentsApi = {
  listForType: (equipmentTypeId: string) =>
    request<EquipmentDocument[]>(`/equipment/types/${equipmentTypeId}/documents`),

  listForEquipment: (equipmentId: string) =>
    request<EquipmentDocumentLists>(`/equipment/units/${equipmentId}/documents`),

  uploadForType: (equipmentTypeId: string, name: string, file: File) =>
    upload(`/equipment/types/${equipmentTypeId}`, name, file),

  uploadForEquipment: (equipmentId: string, name: string, file: File) =>
    upload(`/equipment/units/${equipmentId}`, name, file),

  rename: (id: string, name: string) =>
    request<EquipmentDocument>(`/equipment/documents/${id}`, { method: 'PATCH', ...json({ name }) }),

  getDownloadUrl: (id: string) =>
    request<EquipmentDocumentDownload>(`/equipment/documents/${id}/download`),

  remove: (id: string) =>
    request<{ ok: true }>(`/equipment/documents/${id}`, { method: 'DELETE' }),
}
